// `floor.station.updated` on `venue:{id}:floor` (spec/09-realtime-protocol.md §4, §6.3, §8;
// INFRA-18, #379).
//
// A tile delta for the live floor. Consoles never rebuild the floor from these alone (§4): they
// subscribe, buffer, fetch the snapshot with its `version`, apply what is newer, and fetch the
// snapshot again when a `version` skips. So every one carries the channel's `version` — one counter
// per channel, in Valkey, shared by every node that publishes to it and by the snapshot endpoint
// (F-STN-14), which is the only way "newer than the snapshot" can mean anything.
//
// The gateway publishes it from presence — a station connected, went away, changed mode or title —
// with updates for one station inside 250 ms merged, latest wins (§8). The fields a tile will also
// carry (the session, a call, an update's progress, a reservation) arrive with the features that own
// them, as optional fields: an addition, never a break.
import { z } from "../zod.js";
import { SessionIdSchema, StationIdSchema } from "../ids/schema.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { FLOOR_BOOT_CHANGED, FLOOR_PLAN_UPDATED, FLOOR_SESSION_ENDED, FLOOR_SESSION_STARTED, FLOOR_STATION_UPDATED } from "./constants.js";
import { realtimeMessage } from "./envelope.js";
import { StationForegroundSchema, StationModeSchema, StationSetupProgressSchema } from "./station.js";
export { FLOOR_BOOT_CHANGED, FLOOR_PLAN_UPDATED, FLOOR_SESSION_ENDED, FLOOR_SESSION_STARTED, FLOOR_STATION_UPDATED };
export const FloorStationUpdatedPayloadSchema = z
    .object({
    stationId: StationIdSchema,
    /** Whether the station holds a socket now. */
    online: z.boolean(),
    /** Its last reported mode, or `null` before it has reported one. */
    mode: StationModeSchema.nullable(),
    /** What the station is running now — the title being played, else the foreground program — or `null` (F-CAT-04). */
    title: StationForegroundSchema.nullable(),
    /**
     * The start-up setup's progress while the station is `setting_up` (ADR-0055, F-STN-26): "Setting up · 2 of 3"; `null`
     * otherwise. Optional, an addition: a console that predates it ignores it.
     */
    setup: StationSetupProgressSchema.nullable().optional(),
    /** The channel's version after this change: exactly one more than the last event on the channel. */
    version: z.int().min(1),
})
    .openapi('FloorStationUpdatedPayload', {
    description: 'One station tile changed on the live floor (spec/09 §6.3). Apply only if newer than the snapshot.',
});
export const FloorStationUpdatedMessage = realtimeMessage('evt', FLOOR_STATION_UPDATED, FloorStationUpdatedPayloadSchema);
/**
 * `floor.plan.updated` on `venue:{id}:floor` (F-STN-19, spec/13 §3.4, 2026-10-01): a manager saved one floor's plan in
 * arrange mode, so every console, the staff app and display mode re-read that floor (`GET …/floor-plan?floor=`). It
 * names the floor and the plan's new version (the ETag), never the geometry — a client that already holds that
 * version (the one that saved) skips the read. Outside the channel's tile-version sequence, like `settings.changed`.
 */
export const FloorPlanUpdatedPayloadSchema = z
    .object({
    /** The level that was saved: 0 is the ground floor. */
    floor: z.int().min(0).max(9),
    /** The plan's version after the save — its new ETag. */
    planVersion: z.int().min(1),
})
    .openapi('FloorPlanUpdatedPayload', {
    description: 'One floor’s plan was saved in arrange mode; re-read it if you hold an older version (F-STN-19).',
});
export const FloorPlanUpdatedMessage = realtimeMessage('evt', FLOOR_PLAN_UPDATED, FloorPlanUpdatedPayloadSchema);
/**
 * `floor.session.started` / `floor.session.ended` on `venue:{id}:floor` (spec/09 §6.3, F-ANL-02): a session started
 * or ended at the venue — the summary, not the session. The floor's live figures (occupancy now, sessions today,
 * takings today) re-read `GET …/venues/{v}/floor/live` on it, so a session starting moves the count within a second
 * with no page refetch; the station's tile itself follows `floor.station.updated`. Outside the channel's `version`
 * sequence (they ride the outbox dispatcher, not the gateway's counter), like `alerts.cleared`: a missed one is caught
 * by the figures' own interval.
 */
export const FloorSessionPayloadSchema = z
    .object({
    sessionId: SessionIdSchema,
    stationId: StationIdSchema,
    /** When it started (`started`) or ended (`ended`). */
    at: IsoDateTimeSchema,
    /** Why it ended (`ended` only), as `Session.endReason` names it. */
    reason: z.string().min(1).max(40).optional(),
})
    .openapi('FloorSessionPayload', { description: 'A session started or ended at the venue (spec/09 §6.3, F-ANL-02).' });
export const FloorSessionStartedMessage = realtimeMessage('evt', FLOOR_SESSION_STARTED, FloorSessionPayloadSchema);
export const FloorSessionEndedMessage = realtimeMessage('evt', FLOOR_SESSION_ENDED, FloorSessionPayloadSchema);
/**
 * `floor.boot.changed` on `venue:{id}:floor` (F-BOOT-24, spec/13 §3.8, step C of ADR-0065): a boot PC's state may have
 * changed — it started, its image moved on, the boot server went offline. It carries no boot detail, only the station
 * (null when it may concern every PC, as a boot server's change does), so a member who sees the Floor without
 * `boot:view` re-reads `GET …/boot/floor-states`. Outside the channel's `version` sequence, like `floor.plan.updated`.
 */
export const FloorBootChangedPayloadSchema = z
    .object({ stationId: StationIdSchema.nullable() })
    .openapi('FloorBootChangedPayload', { description: 'A PC’s boot state may have changed; re-read the boot states (no detail).' });
export const FloorBootChangedMessage = realtimeMessage('evt', FLOOR_BOOT_CHANGED, FloorBootChangedPayloadSchema);
//# sourceMappingURL=floor.js.map